import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, ShieldX } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/departments", label: "Departments" },
  { to: "/shows", label: "Shows" },
  { to: "/schedule", label: "Schedule" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/hosts", label: "Hosts" },
  { to: "/team", label: "Team" },
  { to: "/hr-investigations", label: "HR Cases" },
  { to: "/careers", label: "Careers" },
  { to: "/policies", label: "Policies" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-between gap-6 h-16">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <img src="/logo.jpg" alt="Freshy Fresh Productions LLC" className="h-11 w-11 object-cover rounded-sm ring-1 ring-[var(--ff-border)]" />
          <div className="leading-tight hidden sm:block lg:hidden 2xl:block whitespace-nowrap">
            <div className="font-display text-sm tracking-tight">FRESHY FRESH</div>
            <div className="label-eyebrow text-[0.55rem]">Productions LLC</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-3 xl:gap-4 min-w-0">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}-link`}
              className={`font-mono-ui text-xs uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${
                loc.pathname === n.to ? "text-white" : "text-[var(--ff-text-dim)] hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 whitespace-nowrap">
          {user ? (
            user.is_banned ? (
              <>
                <Link to="/banned" className="btn-ghost text-xs bg-[var(--ff-red)] text-white border-[var(--ff-red)]" data-testid="banned-link">
                  <ShieldX size={12} /> Account Banned
                </Link>
                <button className="btn-primary text-xs" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">Logout</button>
              </>
            ) : (
              <>
                {(user.roles?.includes("host")) && (
                  <Link to="/host-studio" className="btn-ghost text-xs !px-4 !tracking-[0.12em]" data-testid="host-studio-link">Host Studio</Link>
                )}
                {(["founder", "executive", "admin", "moderator"].includes(user.role) || user.roles?.includes("employee")) ? (
                  <Link to="/dashboard" className="btn-ghost text-xs !px-4 !tracking-[0.12em]" data-testid="dashboard-link">Dashboard</Link>
                ) : (
                  <Link to="/watchlist" className="btn-ghost text-xs !px-4 !tracking-[0.12em]" data-testid="watchlist-link">
                    My Watchlist
                  </Link>
                )}
                <Link to="/profile" className="btn-ghost text-xs flex items-center gap-2 !px-2" data-testid="profile-link" title="My Profile">
                  {user.discord_avatar
                    ? <img src={user.discord_avatar} alt="" className="w-6 h-6 rounded-full" />
                    : <div className="w-6 h-6 rounded-full bg-[var(--ff-red)] text-white text-[0.55rem] flex items-center justify-center font-display">{(user.discord_username || user.name || "?")[0]}</div>}
                </Link>
                <button className="btn-primary text-xs !px-4 !tracking-[0.12em]" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">Logout</button>
              </>
            )
          ) : (
            <Link to="/login" className="btn-primary text-xs" data-testid="login-link">Sign In</Link>
          )}
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[var(--ff-border)] bg-black px-6 py-4 flex flex-col gap-4">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="font-mono-ui text-xs uppercase tracking-[0.18em]" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
              {n.label}
            </Link>
          ))}
          {user ? (
            <>
              {(user.roles?.includes("host")) && (
                <Link to="/host-studio" onClick={() => setOpen(false)} className="btn-ghost text-xs text-center">Host Studio</Link>
              )}
              {(["founder", "executive", "admin", "moderator"].includes(user.role) || user.roles?.includes("employee")) ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-ghost text-xs text-center">Dashboard</Link>
              ) : (
                <Link to="/watchlist" onClick={() => setOpen(false)} className="btn-ghost text-xs text-center">My Watchlist</Link>
              )}
              <Link to="/profile" onClick={() => setOpen(false)} className="btn-ghost text-xs text-center">My Profile</Link>
              <Link to="/memberships" onClick={() => setOpen(false)} className="btn-ghost text-xs text-center" data-testid="mobile-memberships-link">My Memberships</Link>
              <button className="btn-primary text-xs" onClick={async () => { await logout(); setOpen(false); nav("/"); }}>Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="btn-primary text-xs text-center">Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
}
